/**
 * Shipping Date Checker
 * Calculates the next available shipping/delivery date based on:
 *  - Current time in America/New_York timezone
 *  - Available delivery days: Tuesday (2) to Thursday (4)
 *  - 3PM EST cutoff for next-day delivery
 *  - Configurable blocked dates (holidays) + the day after each blocked date
 *
 * Usage:
 *   var checker = $.shippingDateChecker({ blockedDates: ['2026-07-04', '2026-12-25'] });
 *   var result  = checker.getNextShippingDate();
 *   console.log(result.noticeMessage);
 *   // "GET IT TOMORROW if you order in the next 3hrs 32mins or checkout..."
 *
 *   // Live-updating notice (re-renders every 60s, auto-switches after cutoff):
 *   checker.startNoticeTimer('#shipping-notice');
 *
 * NOTE: No template literals (backticks / ${}) anywhere in this file.
 *       Shopify's Liquid compiler processes .liquid asset files before serving
 *       them, and ${ } inside backtick strings causes compilation failures.
 *       All string building uses plain concatenation instead.
 *
 * NOTE: JSDoc @returns uses {Object} not double-brace syntax.
 *       Liquid parses double-braces as output tags, even inside JS comments.
 */

;(function ($, window, document) {
    'use strict';

    // -------------------------------------------------------------------------
    // Constants
    // -------------------------------------------------------------------------
    var TIMEZONE       = 'America/New_York';
    var CUTOFF_HOUR    = 15;          // 3 PM ET
    var DELIVERY_DAYS  = [2, 3, 4, 5];  // Tue, Wed, Thu  (0 = Sun ... 6 = Sat)
    var MAX_LOOK_AHEAD = 60;          // Safety cap: stop searching after N days
    var TIMER_INTERVAL = 60 * 1000;  // Re-render notice every 60 seconds

    // -------------------------------------------------------------------------
    // Plugin factory
    // -------------------------------------------------------------------------
    $.shippingDateChecker = function (options) {

        var settings = $.extend({
            /**
             * Array of ISO date strings to block, e.g. ['2026-07-04'].
             * The day AFTER each blocked date is also automatically blocked.
             * @type {string[]}
             */
            blockedDates: [],

            /**
             * Suffix appended to every notice message after the countdown.
             * Override to localise or adjust copy.
             * @type {string}
             */
            noticeSuffix: ' or checkout other possible delivery dates in the shopping cart.'
        }, options);

        // Holds the setInterval reference so we can clear it when needed
        var _timerHandle = null;

        // ---------------------------------------------------------------------
        // Private helpers
        // ---------------------------------------------------------------------

        /**
         * Returns a Date object representing "now" in the ET timezone.
         * Intl.DateTimeFormat extracts ET wall-clock parts; we then reconstruct
         * a plain JS Date from those values for reliable arithmetic.
         *
         * @returns {Date}
         */
        function getNowInET() {
            var now = new Date();

            var fmt = new Intl.DateTimeFormat('en-US', {
                timeZone : TIMEZONE,
                year     : 'numeric',
                month    : '2-digit',
                day      : '2-digit',
                hour     : '2-digit',
                minute   : '2-digit',
                second   : '2-digit',
                hour12   : false
            });

            var parts = {};
            fmt.formatToParts(now).forEach(function (item) {
                parts[item.type] = item.value;
            });

            return new Date(
                parseInt(parts.year,   10),
                parseInt(parts.month,  10) - 1,
                parseInt(parts.day,    10),
                parseInt(parts.hour,   10),
                parseInt(parts.minute, 10),
                parseInt(parts.second, 10)
            );
        }

        /**
         * Normalises a Date to a YYYY-MM-DD string using its local values.
         * No timezone shift applied -- always call with a Date already in ET.
         *
         * @param   {Date}   date
         * @returns {string} e.g. "2026-07-04"
         */
        function toISODateString(date) {
            var y = date.getFullYear();
            var m = String(date.getMonth() + 1).padStart(2, '0');
            var d = String(date.getDate()).padStart(2, '0');
            return y + '-' + m + '-' + d;
        }

        /**
         * Builds a Set of all blocked ISO date strings, including the day after
         * each explicitly configured date.
         *
         * @returns {Set.<string>}
         */
        function buildBlockedSet() {
            var blocked = new Set();

            settings.blockedDates.forEach(function (iso) {
                if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
                    console.warn('[shippingDateChecker] Invalid date skipped: ' + iso);
                    return;
                }

                var parts = iso.split('-').map(Number);
                var base  = new Date(parts[0], parts[1] - 1, parts[2]);

                if (isNaN(base.getTime())) {
                    console.warn('[shippingDateChecker] Unparseable date skipped: ' + iso);
                    return;
                }

                // Block the holiday itself
                blocked.add(toISODateString(base));

                // Block the following day automatically
                var dayAfter = new Date(base);
                dayAfter.setDate(dayAfter.getDate() + 1);
                blocked.add(toISODateString(dayAfter));
            });

            return blocked;
        }

        /**
         * Advances a Date by one calendar day (mutates in place).
         *
         * @param {Date} date
         */
        function advanceOneDay(date) {
            date.setDate(date.getDate() + 1);
        }

        /**
         * Returns true if the date is an available delivery day (Tue-Thu)
         * and is not in the blocked set.
         *
         * @param   {Date}         date
         * @param   {Set.<string>} blockedSet
         * @returns {boolean}
         */
        function isAvailable(date, blockedSet) {
            if (!DELIVERY_DAYS.includes(date.getDay())) return false;
            return !blockedSet.has(toISODateString(date));
        }

        /**
         * Formats a Date as a long human-readable string.
         * e.g. "Tuesday, July 7, 2026"
         *
         * @param   {Date}   date
         * @returns {string}
         */
        function formatDate(date) {
            return date.toLocaleDateString('en-US', {
                weekday : 'long',
                year    : 'numeric',
                month   : 'long',
                day     : 'numeric'
            });
        }

        /**
         * Returns only the weekday name for a Date.
         * e.g. "Tuesday"
         *
         * @param   {Date}   date
         * @returns {string}
         */
        function formatWeekday(date) {
            return date.toLocaleDateString('en-US', { weekday: 'long' });
        }

        /**
         * Calculates the remaining minutes from "now" until today's 3PM ET cutoff.
         * If the cutoff has already passed today, returns the minutes until
         * 3PM ET on the NEXT calendar day (i.e. the next delivery window opens).
         *
         * @param   {Date}   now  - Current ET wall-clock Date
         * @returns {Object} { totalMins, hrs, mins, pastTodayCutoff }
         */
        function calcCutoffCountdown(now) {
            var cutoff = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
                CUTOFF_HOUR,
                0, 0, 0
            );

            var pastTodayCutoff = now >= cutoff;

            if (pastTodayCutoff) {
                // Advance cutoff to 3PM tomorrow
                cutoff.setDate(cutoff.getDate() + 1);
            }

            var diffMs    = cutoff - now;
            var totalMins = Math.max(0, Math.floor(diffMs / 60000));
            var hrs       = Math.floor(totalMins / 60);
            var mins      = totalMins % 60;

            return {
                totalMins       : totalMins,
                hrs             : hrs,
                mins            : mins,
                pastTodayCutoff : pastTodayCutoff
            };
        }

        /**
         * Formats a countdown object as a human-readable string.
         * e.g. "3hrs 32mins", "45mins", "1hr 0mins"
         *
         * @param   {Object} countdown - Result of calcCutoffCountdown()
         * @returns {string}
         */
        function formatCountdown(countdown) {
            if (countdown.hrs > 0) {
                var hrLabel = countdown.hrs === 1 ? 'hr' : 'hrs';
                return countdown.hrs + hrLabel + ' ' + countdown.mins + 'mins';
            }
            return countdown.mins + 'mins';
        }

        /**
         * Builds the full notice message string.
         *
         * Before cutoff (and next delivery is tomorrow):
         *   "GET IT TOMORROW if you order in the next 3hrs 32mins or checkout..."
         *
         * After cutoff (or next delivery is later in the week):
         *   "GET IT THIS Tuesday if you order in the next 14hrs 28mins or checkout..."
         *
         * @param   {Date}   now         - Current ET wall-clock Date
         * @param   {Date}   deliveryDate - Next available delivery Date
         * @returns {string}
         */
        function buildNoticeMessage(now, deliveryDate) {
            var countdown    = calcCutoffCountdown(now);
            var countdownStr = formatCountdown(countdown);

            // Before cutoff: earliest delivery is always tomorrow, so say TOMORROW.
            // After cutoff:  tomorrow is no longer reachable, use the weekday name.
            var dayLabel = countdown.pastTodayCutoff
                ? 'THIS ' + formatWeekday(deliveryDate).toUpperCase()
                : 'TOMORROW';

            return (
                'GET IT ' + dayLabel +
                ' if you order in the next ' + countdownStr +
                settings.noticeSuffix
            );
        }

        // ---------------------------------------------------------------------
        // Public API
        // ---------------------------------------------------------------------
        return {

            /**
             * Calculates the next available shipping date and associated notice data.
             * The earliest possible date is always tomorrow -- today is never eligible.
             *
             * @returns {Object|null}
             *   date            {Date}    - Raw Date object for the delivery day
             *   iso             {string}  - YYYY-MM-DD
             *   formatted       {string}  - "Tuesday, July 7, 2026"
             *   cutoffCountdown {Object}  - hrs, mins, totalMins, pastTodayCutoff
             *   noticeMessage   {string}  - Ready-to-display urgency string
             *
             * Returns null if no available date is found within MAX_LOOK_AHEAD days.
             */
            getNextShippingDate: function () {
                var now        = getNowInET();
                var blockedSet = buildBlockedSet();

                // Always start from tomorrow -- today is never a valid delivery date
                var candidate = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate()
                );
                advanceOneDay(candidate);

                for (var i = 0; i < MAX_LOOK_AHEAD; i++) {
                    if (isAvailable(candidate, blockedSet)) {
                        var candidateISO = toISODateString(candidate);
                        var countdown    = calcCutoffCountdown(now);

                        return {
                            date            : new Date(candidate.getTime()),
                            iso             : candidateISO,
                            formatted       : formatDate(candidate),
                            cutoffCountdown : countdown,
                            noticeMessage   : buildNoticeMessage(now, candidate)
                        };
                    }
                    advanceOneDay(candidate);
                }

                console.error('[shippingDateChecker] No available date found within ' + MAX_LOOK_AHEAD + ' days.');
                return null;
            },

            /**
             * Mounts a live-updating notice into a DOM element.
             * Re-renders every 60 seconds so the countdown stays accurate.
             * Automatically transitions the message from "TOMORROW" to
             * "THIS [weekday]" once the 3PM cutoff passes -- no page reload needed.
             *
             * @param {string|jQuery} selector  - Target element (jQuery selector or object)
             * @param {Object}        [opts]    - Optional overrides
             * @param {string}        [opts.loadingText]  - Text shown before first render
             */
            startNoticeTimer: function (selector, opts) {
                var timerOpts   = $.extend({ loadingText: 'Checking delivery dates...' }, opts);
                var $el         = $(selector);

                if (!$el.length) {
                    console.warn('[shippingDateChecker] startNoticeTimer: element not found: ' + selector);
                    return;
                }

                // Stop any previously running timer on this instance
                this.stopNoticeTimer();

                // Show placeholder immediately
                $el.text(timerOpts.loadingText);

                var self = this;

                // Render function -- called on first paint and every tick
                function render() {
                    var result = self.getNextShippingDate();
                    if (result) {
                        $el.text(result.noticeMessage);
                    } else {
                        $el.text('Delivery dates currently unavailable. Please contact us.');
                        self.stopNoticeTimer();
                    }
                }

                render();
                _timerHandle = setInterval(render, TIMER_INTERVAL);
            },

            /**
             * Stops the live-update timer started by startNoticeTimer().
             */
            stopNoticeTimer: function () {
                if (_timerHandle !== null) {
                    clearInterval(_timerHandle);
                    _timerHandle = null;
                }
            },

            /**
             * Checks whether the 3PM ET cutoff has already passed today.
             *
             * @returns {boolean}
             */
            isPastCutoff: function () {
                return getNowInET().getHours() >= CUTOFF_HOUR;
            },

            /**
             * Returns the current ET wall-clock time as a plain Date object.
             *
             * @returns {Date}
             */
            getCurrentETTime: function () {
                return getNowInET();
            },

            /**
             * Returns the full blocked date Set (including auto-generated
             * day-after entries) for debugging or display purposes.
             *
             * @returns {Set.<string>}
             */
            getBlockedDates: function () {
                return buildBlockedSet();
            }
        };
    };

}(jQuery, window, document));


// =============================================================================
// Example usage
// =============================================================================

$(function () {

    var checker = $.shippingDateChecker({
        blockedDates: [
            '2026-07-04', // Independence Day  (Jul 5 auto-blocked)
            '2026-09-08', // 
            '2026-11-26', // Thanksgiving       (Nov 27 auto-blocked)
            '2026-12-25'  // Christmas          (Dec 26 auto-blocked)
        ]
    });

    // --- One-shot read ---
    var result = checker.getNextShippingDate();

    if (result) {
        console.log('Next shipping date : ' + result.formatted);
        console.log('ISO                : ' + result.iso);
        console.log('Past 3PM cutoff?   : ' + result.cutoffCountdown.pastTodayCutoff);
        console.log('Countdown          : ' + result.cutoffCountdown.hrs + 'h ' + result.cutoffCountdown.mins + 'm');
        console.log('Notice message     : ' + result.noticeMessage);
    } else {
        console.error('[shippingDateChecker] No shipping date available.');
    }

    // --- Live-updating notice (re-renders every 60s) ---
    // Mounts to any element with id="shipping-notice"
    checker.startNoticeTimer('#shipping-notice');

    // To stop the timer manually (e.g. when the element is removed):
    // checker.stopNoticeTimer();

});